/**
 * The file page's side panel: whose file it is, then the stored file's own
 * details. What Doppler read off the file (type, company, period) is not
 * shown here.
 */
import React from "react";
import type { LibraryFile } from "../../hooks/useFileLibrary";
import { formatDateTime, formatFileSize } from "../../utils/formatUtils";
import OwnerAvatar from "./OwnerAvatar";

interface FileFactsProps {
	file: LibraryFile;
}

const Section: React.FC<{ title: string; last?: boolean; children: React.ReactNode }> = ({
	title,
	last,
	children,
}) => (
	<section className={`px-5 py-4 ${last ? "" : "border-b border-[#f1f2f4]"}`}>
		<h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.09em] text-text-muted">
			{title}
		</h2>
		{children}
	</section>
);

const Fields: React.FC<{ rows: [string, string][] }> = ({ rows }) => (
	<dl className="grid grid-cols-[110px_1fr] gap-y-1.5 text-[13px]">
		{rows.map(([label, value]) => (
			<React.Fragment key={label}>
				<dt className="text-text-muted">{label}</dt>
				<dd className="truncate text-gray-800" title={value}>
					{value}
				</dd>
			</React.Fragment>
		))}
	</dl>
);

/**
 * Whose file this is: the deal's owner, and who added it when that was
 * someone else (a teammate uploading into a shared deal). Each with their
 * initials bubble and full name.
 */
const People: React.FC<{ file: LibraryFile }> = ({ file }) => {
	const owner = file.dealOwner ?? null;
	const addedBy = file.added_by && file.added_by.id !== owner?.id ? file.added_by : null;
	const rows = [
		...(owner ? [["Owner", owner] as const] : []),
		...(addedBy ? [["Added by", addedBy] as const] : []),
	];
	if (rows.length === 0) return null;
	return (
		<dl className="mb-1.5 grid grid-cols-[110px_1fr] gap-y-1.5 text-[13px]">
			{rows.map(([label, user]) => (
				<React.Fragment key={label}>
					<dt className="text-text-muted">{label}</dt>
					<dd className="flex min-w-0 items-center gap-1.5 text-gray-800">
						<OwnerAvatar name={user.name} decorative />
						<span className="truncate" title={user.name}>
							{user.name || "A teammate"}
						</span>
					</dd>
				</React.Fragment>
			))}
		</dl>
	);
};

const FileFacts: React.FC<FileFactsProps> = ({ file }) => (
	<aside className="w-full flex-shrink-0 overflow-y-auto border-l border-gray-200 bg-white lg:w-[380px]">
		<Section title="Details" last>
			<People file={file} />
			<Fields
				rows={[
					["Size", formatFileSize(file.file_size)],
					["Format", file.mime_type],
					["Added", formatDateTime(file.created_at)],
				]}
			/>
		</Section>
	</aside>
);

export default FileFacts;
